import { reportApiError } from "@/lib/observability/datadog"

export class ApiError extends Error {
  readonly status: number
  readonly detail?: unknown

  constructor(message: string, status: number, detail?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
  }
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])
const CSRF_COOKIE_NAME = "csrftoken"

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null
  const needle = `${CSRF_COOKIE_NAME}=`
  for (const raw of document.cookie.split(";")) {
    const entry = raw.trim()
    if (entry.startsWith(needle)) {
      return decodeURIComponent(entry.slice(needle.length))
    }
  }
  return null
}

function hasHeader(
  headers: Record<string, string>,
  name: string,
): boolean {
  const needle = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === needle) return true
  }
  return false
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase()
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  }

  if (UNSAFE_METHODS.has(method)) {
    if (!hasHeader(headers, "X-CSRFToken")) {
      const token = readCsrfToken()
      if (token) headers["X-CSRFToken"] = token
    }
    // Only default Content-Type for string bodies. FormData / Blob /
    // URLSearchParams need the browser to compute the boundary.
    if (
      typeof init?.body === "string" &&
      !hasHeader(headers, "Content-Type")
    ) {
      headers["Content-Type"] = "application/json"
    }
  }

  let response: Response
  try {
    response = await fetch(path, { ...init, headers })
  } catch (err) {
    // Network-level failure (DNS, offline, CORS, abort). Report it to
    // Datadog before re-throwing so callers' own error handling is
    // unchanged. `status: 0` is the conventional "no response" marker.
    reportApiError({ method, path, status: 0, detail: undefined })
    throw err
  }

  if (!response.ok) {
    let detail: unknown
    try {
      detail = await response.json()
    } catch {
      detail = undefined
    }
    reportApiError({ method, path, status: response.status, detail })
    throw new ApiError(
      `${response.status} ${response.statusText}`,
      response.status,
      detail,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}
